import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, Activity, Trophy, Users,
         TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown,
         ChevronDown, ChevronRight, MessageSquare, History } from 'lucide-react';
import { ChatButton } from './ChatButton';
import ApplicationAttachments from './ApplicationAttachments';

const STATUS_BADGE = {
  pending: { label: 'Ожидает', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  accepted: { label: 'Принята', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Отклонена', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
};

/**
 * Единый блок «Мониторинг», прикреплённый к каждой карточке лота у организатора.
 * Содержание зависит от фазы тендера:
 *  - waiting / collecting → список заявок на участие (Accept / Reject) + первичные условия
 *  - bidding / completed  → live-ранжирование ставок (top-N с приведёнными ценами)
 */
export default function TenderMonitor({ tender }) {
  const phase = tender.phase;
  const isApplicationsPhase = phase === 'waiting' || phase === 'collecting';
  const isBidsPhase = phase === 'bidding' || phase === 'waiting_bids' || phase === 'completed';
  const isCollapsible = phase === 'completed' || tender.is_archived;
  // По умолчанию для завершённых — свёрнуто, для активных — развёрнуто.
  const [open, setOpen] = useState(!isCollapsible);

  // Не показываем для draft/cancelled.
  if (!isApplicationsPhase && !isBidsPhase) return null;

  return (
    <div className="mt-3 pt-3 border-t-2 border-[#0046AD]/15" data-testid={`monitor-${tender.id}`}>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={isCollapsible ? () => setOpen(o => !o) : undefined}
          className={`flex items-center gap-2 ${isCollapsible ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
          data-testid={`monitor-toggle-${tender.id}`}
          aria-expanded={open}
        >
          {isCollapsible && (open
            ? <ChevronDown className="w-3.5 h-3.5 text-[#0046AD]" />
            : <ChevronRight className="w-3.5 h-3.5 text-[#0046AD]" />)}
          <Activity className="w-4 h-4 text-[#0046AD]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#0046AD]">Мониторинг</span>
          <Badge variant="outline" className="text-[10px] rounded-sm bg-blue-50 text-blue-700 border-blue-200">
            {isApplicationsPhase ? 'Заявки на участие' : (phase === 'completed' ? 'Финальные ставки' : 'Идут торги')}
          </Badge>
          {isCollapsible && !open && (
            <span className="text-[10px] text-slate-400 font-normal">— нажмите, чтобы развернуть</span>
          )}
        </button>
      </div>

      {open && (isApplicationsPhase
        ? <ApplicationsList tender={tender} />
        : <BidsList tender={tender} phase={phase} />)}
    </div>
  );
}

// =============================================================
// Applications (waiting/collecting): принять/отклонить
// =============================================================
function ApplicationsList({ tender }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenders/${tender.id}/applications`);
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tender.id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    try {
      await api.put(`/tenders/${tender.id}/applications/${id}`, { status });
      toast.success(status === 'accepted' ? 'Заявка принята' : 'Заявка отклонена');
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pending = items.filter(a => a.status === 'pending').length;

  if (loading) return <div className="text-xs text-slate-400 py-2">Загрузка заявок…</div>;
  if (items.length === 0) {
    return (
      <div className="text-xs text-slate-400 py-3 px-3 bg-slate-50 border border-slate-200 rounded-sm flex items-center gap-2">
        <Users className="w-3.5 h-3.5" />
        Заявок на участие пока нет. Поставщики увидят кнопку «Подать заявку» в своей карточке тендера.
      </div>
    );
  }

  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-1.5">
        Всего заявок: <span className="font-semibold text-slate-700">{items.length}</span>
        {pending > 0 && <> · на рассмотрении: <span className="font-semibold text-amber-700">{pending}</span></>}
      </div>
      <div className="space-y-1.5">
        {items.map(a => {
          const sb = STATUS_BADGE[a.status] || STATUS_BADGE.pending;
          const Icon = sb.icon;
          return (
            <div key={a.id} className="flex items-start gap-2 px-3 py-2 rounded-sm bg-slate-50 border border-slate-200"
              data-testid={`application-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800 truncate">{a.company_name || a.user_email}</span>
                  <Badge variant="outline" className={`text-[10px] rounded-sm ${sb.cls}`}>
                    <Icon className="w-2.5 h-2.5 mr-1 inline" />
                    {sb.label}
                  </Badge>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {a.user_email} · {a.contact_name || '—'} · {new Date(a.created_at).toLocaleString('ru-RU')}
                </div>
                {(a.bid_price_per_ton != null || a.bid_weight_in_container != null ||
                  a.bid_prepay_percent != null || a.bid_delay_percent != null || a.bid_comment) && (
                  <div className="mt-1.5 grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px] bg-white border border-slate-100 rounded-sm px-2 py-1.5">
                    {a.bid_price_per_ton != null && (
                      <div><span className="text-slate-400">Цена за ед.:</span> <span className="font-semibold text-slate-700">{a.bid_price_per_ton} {tender.currency}</span></div>
                    )}
                    {a.bid_weight_in_container != null && (
                      <div><span className="text-slate-400">Вес/конт:</span> <span className="font-semibold text-slate-700">{a.bid_weight_in_container} {tender.volume_unit || 'т'}</span></div>
                    )}
                    {a.bid_prepay_percent != null && (
                      <div><span className="text-slate-400">Предоплата:</span> <span className="font-semibold text-slate-700">{a.bid_prepay_percent}%/{a.bid_prepay_days || 0}д</span></div>
                    )}
                    {a.bid_delay_percent != null && (
                      <div><span className="text-slate-400">Отсрочка:</span> <span className="font-semibold text-slate-700">{a.bid_delay_percent}%/{a.bid_delay_days || 0}д</span></div>
                    )}
                    {a.bid_comment && (
                      <div className="col-span-2 lg:col-span-4 mt-0.5"><span className="text-slate-400">Комментарий:</span> <span className="text-slate-700">{a.bid_comment}</span></div>
                    )}
                  </div>
                )}
                <ApplicationAttachments applicationId={a.id} canEdit={tender.can_manage !== false} compact />
              </div>
              <div className="flex flex-col gap-1 shrink-0 self-center">
                <ChatButton tenderId={tender.id} supplierEmail={a.user_email} />
                {tender.can_manage !== false && (
                  a.status === 'pending' ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decide(a.id, 'accepted')}
                        data-testid={`application-accept-${a.id}`}
                        className="rounded-sm text-[11px] h-7 px-2.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Принять
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(a.id, 'rejected')}
                        data-testid={`application-reject-${a.id}`}
                        className="rounded-sm text-[11px] h-7 px-2.5 text-rose-700 border-rose-300 hover:bg-rose-50 font-semibold">
                        <XCircle className="w-3 h-3 mr-1" /> Отклонить
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => decide(a.id, 'pending')}
                      data-testid={`application-revert-${a.id}`}
                      className="rounded-sm text-[10px] h-7 px-2 text-slate-500 hover:text-slate-700">
                      Отменить решение
                    </Button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================
// Bids (bidding/waiting_bids/completed): live ranking — расширенный вид
// для организатора с полной детализацией.
// =============================================================
function BidsList({ tender, phase }) {
  const [rankings, setRankings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, hs] = await Promise.all([
        api.get(`/tenders/${tender.id}/rankings`),
        api.get(`/tenders/${tender.id}/bids-history`).catch(() => ({ data: [] })),
      ]);
      setRankings(rs.data || []);
      setHistory(hs.data || []);
    } catch {} finally { setLoading(false); }
  }, [tender.id]);

  useEffect(() => {
    load();
    if (phase === 'bidding') {
      const id = setInterval(load, 10000);
      return () => clearInterval(id);
    }
  }, [load, phase]);

  if (loading) return <div className="text-xs text-slate-400 py-2">Загрузка ставок…</div>;
  if (rankings.length === 0) {
    return (
      <div className="text-xs text-slate-400 py-3 px-3 bg-slate-50 border border-slate-200 rounded-sm">
        {phase === 'waiting_bids'
          ? 'Торги ещё не начались — ставки появятся после начала.'
          : 'Ставок по этому тендеру нет.'}
      </div>
    );
  }

  // Группируем историю по поставщику для мини-спарклайна и счётчика обновлений.
  // Дополнительно сортируем каждую группу по реальному времени —
  // защита от смеси таймзон в исторических данных.
  const histByEmail = {};
  for (const h of history) {
    const k = h.user_email;
    if (!histByEmail[k]) histByEmail[k] = [];
    histByEmail[k].push(h);
  }
  for (const k of Object.keys(histByEmail)) {
    histByEmail[k].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
  }

  return (
    <div className="space-y-2">
      {rankings.map((r, idx) => (
        <BidRow key={r.bid_id || r.user_email || idx}
          rank={r.rank != null ? r.rank : (idx + 1)}
          bid={r}
          tender={tender}
          history={histByEmail[r.user_email] || []} />
      ))}
    </div>
  );
}

function BidRow({ rank, bid, tender, history }) {
  const cur = tender.currency || '';
  const unit = tender.volume_unit || 'т';
  const noBid = !!bid.no_bid;
  const isWinner = !noBid && rank === 1;
  const deltaCap = bid.delta_to_cap;
  const deltaInit = bid.delta_from_initial;
  const [expanded, setExpanded] = useState(false);

  // Спарклайн динамики adjusted_price (если есть история ≥ 2 точек).
  const points = (history || [])
    .filter(h => typeof h.adjusted_price === 'number')
    .map(h => h.adjusted_price);
  const sparkSvg = (() => {
    if (noBid) return null;
    if (points.length < 2) return null;
    const min = Math.min(...points), max = Math.max(...points);
    const span = max - min || 1;
    const W = 100, H = 24;
    const step = W / (points.length - 1);
    const path = points
      .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(H - ((v - min) / span) * H).toFixed(1)}`)
      .join(' ');
    const last = points[points.length - 1];
    const first = points[0];
    const trendUp = last < first; // ниже = лучше → тренд вниз = поставщик улучшается
    const color = trendUp ? '#16a34a' : (last > first ? '#dc2626' : '#64748b');
    return (
      <svg width={W} height={H} className="shrink-0" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
      </svg>
    );
  })();

  const expectedPrice = tender.expected_price || 0;
  const aboveCap = !noBid && expectedPrice > 0 && (bid.adjusted_price ?? 0) > expectedPrice;

  return (
    <div className={`px-3 py-2 rounded-sm border ${
        noBid ? 'bg-slate-50 border-slate-200 border-dashed'
              : isWinner ? 'bg-amber-50/60 border-amber-300'
              : aboveCap ? 'bg-rose-50/40 border-rose-200'
              : 'bg-white border-slate-200'
      }`}
      data-testid={`bid-row-${bid.bid_id || bid.user_email || rank}`}>
      <div className="flex items-start gap-3">
        {/* Ранг */}
        <div className="shrink-0 pt-0.5">
          {noBid ? (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-500 font-bold text-xs">
              —
            </span>
          ) : isWinner ? (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-200 text-amber-800 font-bold">
              <Trophy className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-600 font-bold text-xs">
              {rank}
            </span>
          )}
        </div>

        {/* Данные ставки */}
        <div className="flex-1 min-w-0">
          {/* Шапка: компания + email */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-slate-900 truncate">{bid.company_name || bid.user_email || bid.alias}</span>
            {bid.is_alternative && (
              <Badge variant="outline" className="text-[10px] rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200"
                data-testid={`alt-bid-badge-${bid.bid_id || bid.id || bid.user_email}`}>
                {bid.option_name || 'Альтернативное предложение'}
              </Badge>
            )}
            {!bid.is_alternative && bid.option_name && bid.option_name !== 'Основное предложение' && (
              <Badge variant="outline" className="text-[10px] rounded-sm bg-slate-50 text-slate-700 border-slate-200">
                {bid.option_name}
              </Badge>
            )}
            {bid.contact_name && <span className="text-[10px] text-slate-500 truncate">· {bid.contact_name}</span>}
            <span className="text-[10px] text-slate-400 truncate">{bid.user_email}</span>
            {noBid && (
              <Badge variant="outline" className="text-[9px] rounded-sm bg-slate-100 text-slate-600 border-slate-200">
                Ставка не подана
              </Badge>
            )}
            {aboveCap && (
              <Badge variant="outline" className="text-[9px] rounded-sm bg-rose-50 text-rose-700 border-rose-200" title="Приведённая цена выше ценового потолка">
                выше потолка
              </Badge>
            )}
            {!noBid && history.length > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                className="inline-flex items-center gap-1 text-[10px] rounded-sm bg-slate-50 hover:bg-slate-100 text-slate-700 px-1.5 py-0.5 border border-slate-200"
                data-testid={`bid-history-toggle-${bid.user_email}`}
                title="История ставок этого поставщика"
              >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <History className="w-3 h-3" /> История ({history.length})
              </button>
            )}
            {bid.user_email && (
              <ChatButton tenderId={tender.id} supplierEmail={bid.user_email} className="ml-auto" />
            )}
          </div>

          {/* Комментарий из заявки на участие (если есть) */}
          {bid.application_comment && (
            <div className="mt-1 flex items-start gap-1.5 text-[11px] bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
              <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-amber-700" />
              <div className="min-w-0">
                <span className="text-amber-700 font-semibold">Комментарий поставщика к заявке:</span>{' '}
                <span className="text-slate-800">{bid.application_comment}</span>
              </div>
            </div>
          )}

          {!noBid && (() => {
            const isLogistics = (tender.tender_direction || tender.direction || '').toLowerCase() === 'logistics';
            return (
            <>
              {/* Финансовая разбивка: Цена + U + E = Приведённая (для закупки) или просто Цена (для логистики) */}
              <div className="mt-1 grid grid-cols-12 gap-x-2 gap-y-1 text-[11px]">
                <Cell label="Цена за ед." value={`${(bid.price_per_ton ?? 0).toFixed(2)} ${cur}`}
                  className={isLogistics ? "col-span-6 lg:col-span-4 font-bold text-[#0046AD]" : "col-span-3 lg:col-span-2"}
                  big={isLogistics} />
                {!isLogistics && (
                  <>
                    <Cell label="Логистика (U)" value={`${(bid.indicator_U ?? 0) >= 0 ? '+' : ''}${(bid.indicator_U ?? 0).toFixed(2)}`} className="col-span-3 lg:col-span-2 text-blue-700" />
                    <Cell label="Деньги (E)" value={`${(bid.indicator_E ?? 0) >= 0 ? '+' : ''}${(bid.indicator_E ?? 0).toFixed(2)}`} className={`col-span-3 lg:col-span-2 ${(bid.indicator_E ?? 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`} />
                    <Cell label="Приведённая" value={`${(bid.adjusted_price ?? 0).toFixed(2)} ${cur}/${unit}`} className="col-span-3 lg:col-span-3 font-bold text-[#0046AD]" big />
                  </>
                )}
                <div className={`col-span-12 lg:${isLogistics ? 'col-span-8' : 'col-span-3'} flex items-center justify-end gap-1.5`}>
                  {sparkSvg}
                </div>
              </div>

              {/* Условия оплаты */}
              <div className="mt-1.5 grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-0.5 text-[11px] bg-slate-50 border border-slate-100 rounded-sm px-2 py-1">
                <KV label="Вес/конт" value={`${bid.weight_in_container ?? '—'} ${unit}`} />
                <KV label="Предоплата" value={`${bid.prepay_percent ?? 0}% / ${bid.prepay_days ?? 0} дн`} />
                <KV label="Отсрочка" value={`${bid.delay_percent ?? 0}% / ${bid.delay_days ?? 0} дн`} />
                {(tender.tender_direction || tender.direction || '').toLowerCase() !== 'logistics' && (
                  <KV label="Стоим. денег"
                      value={`D = ${(tender.money_cost_day ?? 0)}%/день`}
                      title="Стоимость денег организатора (D) — общая для всех ставок этого тендера" />
                )}
              </div>

              {/* Метрики: динамика и разница с потолком */}
              <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px]">
                {deltaInit != null && (
                  <span className={`inline-flex items-center gap-1 ${deltaInit < 0 ? 'text-emerald-700' : deltaInit > 0 ? 'text-rose-700' : 'text-slate-500'}`}
                    title="Изменение приведённой цены с момента первой ставки">
                    {deltaInit < 0 ? <TrendingDown className="w-3 h-3" /> : deltaInit > 0 ? <TrendingUp className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    Динамика: {deltaInit > 0 ? '+' : ''}{deltaInit.toFixed(2)} {cur}
                  </span>
                )}
                {deltaCap != null && (
                  <span className={`inline-flex items-center gap-1 ${deltaCap >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                    title="Разница приведённой цены с ценовым потолком (плюс = ниже потолка)">
                    {deltaCap >= 0 ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                    Δ к потолку: {deltaCap > 0 ? '−' : '+'}{Math.abs(deltaCap).toFixed(2)} {cur}
                  </span>
                )}
                {bid.updated_at && (
                  <span className="text-slate-400 ml-auto">
                    Обновлено: {new Date(bid.updated_at).toLocaleString('ru-RU')}
                  </span>
                )}
              </div>

              {/* Развёрнутая история шагов поставщика */}
              {expanded && history.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-sm bg-white">
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 bg-slate-50">
                    История по поставщику ({history.length})
                  </div>
                  <div className="divide-y divide-slate-100">
                    {history.map((h, i) => {
                      const isApp = h.action === 'application';
                      const isCreate = h.action === 'create';
                      return (
                        <div key={h.id || i} className="px-3 py-1.5 text-[11px] grid grid-cols-12 gap-x-2">
                          <div className="col-span-2 text-slate-400">
                            {h.created_at ? new Date(h.created_at).toLocaleString('ru-RU') : '—'}
                          </div>
                          <div className="col-span-2">
                            <Badge variant="outline" className={`text-[9px] rounded-sm ${
                              isApp ? 'bg-violet-50 text-violet-700 border-violet-200'
                              : isCreate ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-slate-50 text-slate-600 border-slate-200'
                            }`}>
                              {isApp ? 'Заявка' : isCreate ? 'Первая ставка' : 'Обновление'}
                            </Badge>
                          </div>
                          <div className="col-span-2 tabular-nums">
                            {h.price_per_ton != null ? `${Number(h.price_per_ton).toFixed(2)} ${cur}` : '—'}
                          </div>
                          <div className="col-span-2 tabular-nums text-[#0046AD] font-semibold">
                            {h.adjusted_price != null ? `${Number(h.adjusted_price).toFixed(2)}` : '—'}
                          </div>
                          <div className="col-span-2 text-slate-500">
                            {h.prepay_percent != null ? `${h.prepay_percent}%/${h.prepay_days || 0}д` : '—'}
                            {h.delay_percent != null && ` ↔ ${h.delay_percent}%/${h.delay_days || 0}д`}
                          </div>
                          <div className="col-span-2 truncate text-slate-600" title={h.comment || ''}>
                            {h.comment ? <><MessageSquare className="w-3 h-3 inline mr-1 text-slate-400" />{h.comment}</> : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, className = '', big = false }) {
  return (
    <div className={`flex flex-col ${className}`}>
      <span className="text-[9px] text-slate-400 uppercase tracking-wider leading-tight">{label}</span>
      <span className={`tabular-nums ${big ? 'text-sm' : 'text-[11px]'} leading-tight`}>{value}</span>
    </div>
  );
}

function KV({ label, value, title }) {
  return (
    <div title={title}>
      <span className="text-slate-400">{label}:</span> <span className="font-semibold text-slate-700">{value}</span>
    </div>
  );
}
